import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as api from '../services/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      setPermissions({});
      setLoading(false);
    }
  }, [user, location.pathname]);

  const loadPermissions = async () => {
    try {
      const res = await api.getMyPermissions();
      setPermissions(res.data.permissions);
    } catch {
      setPermissions({});
    } finally {
      setLoading(false);
    }
  };

  const can = (module, action) => {
    if (user?.role === 'admin' || user?.is_superadmin) return true;
    return permissions[module]?.[action] || false;
  };

  const canView = (module) => can(module, 'view');
  const canCreate = (module) => can(module, 'create');
  const canEdit = (module) => can(module, 'edit');
  const canDelete = (module) => can(module, 'delete');

  const refreshPermissions = async () => {
    if (user) { setLoading(true); await loadPermissions(); }
  };

  return (
    <PermissionsContext.Provider value={{ permissions, loading, can, canView, canCreate, canEdit, canDelete, refreshPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be within PermissionsProvider');
  return ctx;
};
